import { useCallback } from 'react';
import { LoginRequest, useAuthStore, useLogin } from '@/entities/auth';
import { useLazyMyProfile } from '@/entities/profile';
import { updateUiSettings } from '@/entities/profile/model/api/api';
import { useSettingsStore } from '@/entities/settings';
import { UiSettings } from '@/entities/settings/models/types';

/**
 * Проверяет, является ли объект пустым (null, undefined или {}).
 * Тайп-гард: на false-ветке TypeScript сужает тип до `UiSettings`.
 */
const isEmptyUiSettings = (value: UiSettings | null | undefined): value is null | undefined =>
  value == null || Object.keys(value).length === 0;

/**
 * Читает сохранённые настройки пользователя из legacy-ключа localStorage['settings'].
 * Возвращает null, если данных нет или они в неожиданном формате.
 */
const readLegacyUserSettings = (userId: string): UiSettings | null => {
  try {
    const raw = localStorage.getItem('settings');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { settings?: Record<string, UiSettings> } };
    return parsed?.state?.settings?.[userId] ?? null;
  } catch {
    return null;
  }
};

/**
 * Инициализирует настройки пользователя после успешной авторизации.
 * Выполняет миграцию из localStorage на бэкенд при первом запуске после деплоя.
 */
const applyUserSettings = async (userId: string, uiSettings: UiSettings | null | undefined) => {
  const { initUserSettings } = useSettingsStore.getState();
  const safeId = String(userId);

  if (isEmptyUiSettings(uiSettings)) {
    // Проверяем наличие legacy-данных для однократной миграции
    const legacySettings = readLegacyUserSettings(safeId);
    if (legacySettings != null) {
      try {
        // Мигрируем настройки из localStorage в бэкенд
        await updateUiSettings(legacySettings);
      } catch (error) {
        console.error('Ошибка миграции настроек в бэкенд:', error);
      }
    }
    // Стор наполняется и для пустого набора: иначе вошедший донаследовал бы от предыдущего
    // пользователя этого браузера то, что применяется глобально, — прежде всего тему
    initUserSettings(safeId, legacySettings ?? {});
    // Удаляем legacy-ключ из localStorage (очистка устаревших данных)
    localStorage.removeItem('settings');
    return;
  }

  // Инициализируем стор настройками с бэкенда
  initUserSettings(safeId, uiSettings);
  // Удаляем legacy-ключ из localStorage (очистка устаревших данных)
  localStorage.removeItem('settings');
};

/**
 * Hook for accessing authentication functionality
 *
 * Provides methods for login, logout, and accessing user data and authentication state.
 * This hook is a wrapper around the authentication store that provides a more convenient API.
 */
export const useAuth = () => {
  const { user, isLoading, error, isSessionExpired, logout, resetError } = useAuthStore();

  const { mutateAsync: login } = useLogin();
  const { refetch: getCurrentUser } = useLazyMyProfile();

  /**
   * Login with email and password
   * @param credentials - Login credentials (email and password)
   */
  const handleLogin = useCallback(
    async (credentials: LoginRequest) => {
      try {
        // Сообщение о завершившейся сессии снимается на первой же попытке войти заново
        useAuthStore.setState({ isLoading: false, error: null, isSessionExpired: false });

        // Вызываем API логина и сохраняем флаг временного пароля
        const loginResponse = await login(credentials);
        // Get user data after successful login
        const userData = await getCurrentUser();

        // Инициализируем настройки ДО снятия флага загрузки — чтобы authorized-часть
        // приложения рендерилась уже с готовыми ui-settings
        await applyUserSettings(String(userData.id), userData.uiSettings);

        useAuthStore.setState({
          user: userData,
          isLoading: false,
          isTempPassword: loginResponse.isTempPassword,
        });
      } catch (error) {
        useAuthStore.setState({
          isLoading: false,
          error: error instanceof Error ? error.message : 'Ошибка при входе в систему',
          user: null,
        });

        throw error;
      }
    },
    [login, getCurrentUser],
  );

  /**
   * Logout the current user
   */
  const handleLogout = useCallback(async () => {
    useSettingsStore.getState().clearServiceToken();
    await logout();
    window.location.href = '/';
  }, [logout]);

  /**
   * Check if the user is authenticated by calling /auth/me
   */
  const handleCheckAuth = useCallback(async () => {
    // Сессия, завершившаяся во время работы, проверкой не восстанавливается: пользователю
    // уже сказано, что работа прекращена, и вернуть его в приложение может только новый вход.
    // Проверка повторяется, когда сброс кэша перемонтирует дерево приложения
    if (useAuthStore.getState().isSessionExpired) {
      useAuthStore.setState({ user: null, isLoading: false });
      return;
    }

    try {
      useAuthStore.setState({ isLoading: true, error: null });

      // Get current user data
      const userData = await getCurrentUser();

      // Инициализируем настройки ДО снятия флага загрузки — чтобы authorized-часть
      // приложения рендерилась уже с готовыми ui-settings
      await applyUserSettings(String(userData.id), userData.uiSettings);

      useAuthStore.setState({
        user: userData,
        isLoading: false,
      });
    } catch {
      useAuthStore.setState({
        user: null,
        isLoading: false,
      });
    }
  }, [getCurrentUser]);

  /**
   * Reset any error messages
   */
  const handleResetError = useCallback(() => {
    resetError();
  }, [resetError]);

  return {
    // State
    user,
    // Авторизован тот, чьи данные лежат в сторе: отказ по неавторизованности их сбрасывает,
    // и приложение сразу возвращается ко входу
    isAuthenticated: user !== null,
    isLoading,
    error,
    isSessionExpired,

    // Actions
    login: handleLogin,
    logout: handleLogout,
    checkAuth: handleCheckAuth,
    resetError: handleResetError,
  };
};
